import React, { useEffect, useState } from "react";
import { BoxProps, styled } from "@mui/system";
import photo from "../../Assets/images/svg/Icons/file-image-svgrepo-com.svg";
import {
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  MenuItem,
  Select,
  Chip,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { GridLegacy as Grid } from "@mui/material";
import "../../Components/Common/Styles/Formstyles.css";
import SuccessDialog, {
  useSuccessDialog,
} from "../../Components/Common/SuccesMessages/SuccessDialog";
import ErrorMessage from "../../Components/Common/ErrorMessages/ErrorMessage";
import { basicInfoSchema } from "../../Components/Validation";
import * as yup from "yup";
import { useCategory } from "../../Components/Common/Context/Context";
import { FormDataType } from "./FormTypes";

interface BasicInfoFormProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  handleNext: () => void;
  handleCancel: () => void;
  provider?: any;
}

type PortfolioItem = {
  id?: string;
  mediaType: "image";
  media?: string;
  previewUrl?: string;
  file?: File;
};

const UploadBox = styled(Box)<BoxProps>({
  border: "2px dashed #ccc",
  borderRadius: "10px",
  padding: "20px",
  textAlign: "center",
  cursor: "pointer",
  width: "100%",
  height: 150,
});

const ImagePreview = styled("img")({
  width: "80px",
  height: "80px",
  borderRadius: "8px",
  objectFit: "cover",
});

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  formData,
  setFormData,
  handleNext,
  handleCancel,
  provider
}) => {
  const [, setErrorOpen] = useState(false);
  const {
    open: successOpen,
    message,
    closeSuccess,
  } = useSuccessDialog();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const { allCategories } = useCategory();

  // field validation
  const validateField = async (field: string, value: any) => {
    try {
      const schemaField = yup.reach(basicInfoSchema, field) as yup.Schema<any>;
      await schemaField.validate(value);
      setErrors((prev) => ({ ...prev, [field]: "" }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, [field]: err.message }));
    }
  };

  const handleBlur = (field: string, value: any) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  // cleanup preview URLs
  useEffect(() => {
    return () => {
      formData.photos?.forEach((photo: PortfolioItem) => {
        if (photo.previewUrl) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
    };
  }, [formData.photos]);

  // populate categories in edit mode
  useEffect(() => {
    if (!provider || allCategories.length === 0) return;

    const providerCategoryIds = provider.categories?.length
      ? JSON.parse(provider.categories)
      : provider.categoriesDetail?.map((cat: any) => cat.id) || [];

    const matchedCategories = allCategories.filter((category) =>
      providerCategoryIds.includes(category.id)
    );

    setFormData((prev: FormDataType) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        providerName: provider.providerName,
        contactPerson: provider.contactPerson,
        email: provider.email,
        phoneNumber: provider.phone,
        aboutCompany: provider.aboutCompany || "",
        categoriesOffered: matchedCategories,
      },
      providerId: provider.id,
    }));
  }, [provider, allCategories]);

  // field handler
  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        [field]: value,
      },
    }));
  };

  // submit
  const handleNextClick = async () => {
    try {
      await basicInfoSchema.validate(formData.basicInfo, { abortEarly: false });
      setErrors({});
      // showSuccess("Saving Basic Info...");
      // await handleSave(); //  triggers POST/PATCH from ProviderForm
      setTimeout(() => {
        closeSuccess();
        handleNext();
      }, 800);
    } catch (err: any) {
      if (err.inner) {
        const newErrors: { [key: string]: string } = {};
        const newTouched: { [key: string]: boolean } = {};
        err.inner.forEach((validationErr: any) => {
          newErrors[validationErr.path] = validationErr.message;
          newTouched[validationErr.path] = true;
        });
        setErrors(newErrors);
        setTouched(newTouched);
      } else {
        console.error("Validation error:", err);
        setErrorOpen(true);
      }
    }
  };

  return (
    <Box sx={{ px: 2, py: 3 }}>
      <Grid container spacing={3}>
        {/* Provider Name */}
        <Grid item xs={12} md={4}>
          <Typography className="form-input">Provider</Typography>
          <TextField
            className="form-input"
            fullWidth
            placeholder="Type here"
            variant="standard"
            value={formData.basicInfo.providerName || ""}
            onChange={(e) => handleChange("providerName", e.target.value)}
            onBlur={() =>
              handleBlur("providerName", formData.basicInfo.providerName)
            }
            error={touched.providerName && Boolean(errors.providerName)}
          />
          <ErrorMessage
            touched={touched.providerName}
            error={errors.providerName}
          />
        </Grid>

        {/* Contact Person */}
        <Grid item xs={12} md={4}>
          <Typography className="form-input">Contact Person</Typography>
          <TextField
            className="form-input"
            fullWidth
            placeholder="Type here"
            variant="standard"
            value={formData.basicInfo.contactPerson || ""}
            onChange={(e) => handleChange("contactPerson", e.target.value)}
            onBlur={() =>
              handleBlur("contactPerson", formData.basicInfo.contactPerson)
            }
            error={!!touched.contactPerson && !!errors.contactPerson}
            helperText={
              <ErrorMessage
                touched={touched.contactPerson}
                error={errors.contactPerson}
              />
            }
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} md={4}>
          <Typography className="form-input">Email ID</Typography>
          <TextField
            className="form-input"
            fullWidth
            placeholder="Type here"
            variant="standard"
            value={formData.basicInfo.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => handleBlur("email", formData.basicInfo.email)}
            error={!!touched.email && !!errors.email}
            helperText={
              <ErrorMessage touched={touched.email} error={errors.email} />
            }
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12} md={4}>
          <Typography className="form-input">Phone Number</Typography>
          <TextField
            className="form-input"
            fullWidth
            placeholder="Type here"
            variant="standard"
            value={formData.basicInfo.phoneNumber || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 10);
              handleChange("phoneNumber", value);
            }}
            onBlur={() =>
              handleBlur("phoneNumber", formData.basicInfo.phoneNumber)
            }
            error={!!touched.phoneNumber && !!errors.phoneNumber}
            helperText={
              <ErrorMessage
                touched={touched.phoneNumber}
                error={errors.phoneNumber}
              />
            }
          />
        </Grid>

        {/* Category */}
        <Grid item xs={12} md={4}>
          <Typography className="form-input">Category</Typography>
          <FormControl
            fullWidth
            variant="standard"
            error={!!touched.categoriesOffered && !!errors.categoriesOffered}
          >
            <Select
              className="form-input"
              multiple
              displayEmpty
              value={
                Array.isArray(formData.basicInfo.categoriesOffered)
                  ? formData.basicInfo.categoriesOffered.map((c: any) =>
                    typeof c === "string" ? c : c.id.toString()
                  )
                  : []
              }
              onChange={(e) => {
                const selectedIds = (e.target.value as string[]).map((id) =>
                  id.toString()
                );
                const selectedCategories = allCategories.filter((c) =>
                  selectedIds.includes(c.id.toString())
                );
                handleChange("categoriesOffered", selectedCategories);
              }}
              renderValue={(selected: any) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <span style={{ color: '#9e9e9e' }}>Select category</span> // dim gray color
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((id) => {
                      const category = allCategories.find((c) => c.id === id);
                      if (!category) return null;
                      return (
                        <Chip
                          key={id}
                          label={category.name}
                          onDelete={(event) => {
                            event.stopPropagation();
                            const updatedCategories =
                              formData.basicInfo.categoriesOffered.filter(
                                (c: any) => c.id !== id
                              );
                            handleChange(
                              "categoriesOffered",
                              updatedCategories
                            );
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                        />
                      );
                    })}
                  </Box>
                );
              }}
            >
              {allCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>

            {touched.categoriesOffered && errors.categoriesOffered && (
              <FormHelperText>{errors.categoriesOffered}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* About Company */}
        <Grid item xs={12}>
          <Typography className="form-input">About Company</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Type here..."
            variant="outlined"
            value={formData.basicInfo.aboutCompany || ""}
            onChange={(e) => handleChange("aboutCompany", e.target.value)}
          />
        </Grid>

        {/* Provider Image Upload */}
        <Grid item xs={12}>
          <Typography className="form-input" mt={3}>
            Upload Provider Image
          </Typography>

          <UploadBox component="label">
            <img src={photo} alt="upload" style={{ marginTop: 15, width: 40 }} />
            <Typography fontSize="14px" mt={1} fontFamily="Poppins">
              Drag and drop photo or{" "}
              <span style={{ color: "#3E93E8", cursor: "pointer" }}>
                Choose file
              </span>
            </Typography>
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleChange("image", file);
                }
              }}
            />
          </UploadBox>

          {formData.basicInfo.image && (
            <Box mt={2}>
              <ImagePreview
                src={
                  formData.basicInfo.image instanceof File
                    ? URL.createObjectURL(formData.basicInfo.image)
                    : formData.basicInfo.image
                }
                alt="preview"
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          gap: 2,
          mt: 3,
        }}
      >
        <Button onClick={handleCancel} className="btn-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleNextClick}
          className="btn-next"
        >
          Next
        </Button>
      </Box>

      <SuccessDialog
        open={successOpen}
        onClose={closeSuccess}
        message={message}
      />
    </Box>
  );
};

export default BasicInfoForm;
